import type { ModifierGroupView, ModifierOptionView } from "@kopi/contracts";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@kopi/ui/components/card";
import { toast } from "@kopi/ui/components/sonner";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PencilIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { ActiveToggle } from "@/components/active-toggle";
import { catalogueError, selectionRule } from "@/components/catalogue";
import {
  EditModifierGroupDialog,
  ModifierOptionDialog,
  storeModifierGroup,
} from "@/components/modifier-groups";
import { Page, PageHeader } from "@/components/page-header";
import { catalogue } from "@/i18n/messages/catalogue";
import { useMessages } from "@/i18n/provider";
import { api, unwrap } from "@/lib/api";
import { modifierGroupQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/catalogue/modifiers/$groupId")({
  staticData: {
    section: "workspace",
    parent: { label: "nav.catalogue", to: "/catalogue" },
    crumb: "page.modifierGroup",
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(modifierGroupQuery(params.groupId)),
  component: ModifierGroupPage,
});

function ModifierGroupPage() {
  const { t } = useMessages(catalogue);
  const { groupId } = Route.useParams();
  const { data: group } = useSuspenseQuery(modifierGroupQuery(groupId));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const toggle = useMutation({
    mutationFn: async () =>
      unwrap(await api.v1["modifier-groups"]({ groupId: group.id }).patch({ active: !group.active })),
    onSuccess: (updated) => storeModifierGroup(queryClient, updated),
    onError: (error) => toast.error(catalogueError(t, error)),
  });

  return (
    <Page width="narrow">
      <PageHeader
        eyebrow={`${t("nav.catalogue")} · ${t("page.modifierGroup")}`}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {group.name}
            {!group.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
          </span>
        }
        subtitle={selectionRule(t, group)}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("catalogue.group.editGroup")}
            </Button>
            {/* Groups are never deleted: orders will keep the options they sold (CAT-02). */}
            <ActiveToggle
              active={group.active}
              title={t("catalogue.group.deactivateTitle", { name: group.name })}
              description={t("catalogue.group.deactivateHint")}
              confirmLabel={t("catalogue.group.deactivate")}
              disabled={toggle.isPending}
              onChange={() => toggle.mutate()}
            />
          </>
        }
      />
      <Options group={group} />
      {editing && <EditModifierGroupDialog group={group} onClose={() => setEditing(false)} />}
    </Page>
  );
}

function Options(props: { group: ModifierGroupView }) {
  const { t } = useMessages(catalogue);
  const [dialog, setDialog] = useState<{ option?: ModifierOptionView } | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("catalogue.modifiers.options")}</CardTitle>
        <CardDescription>{t("catalogue.group.optionsHint")}</CardDescription>
        <CardAction>
          <Button type="button" variant="outline" onClick={() => setDialog({})}>
            <PlusIcon />
            {t("catalogue.modifiers.addOption")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {/* Not virtualised (CODING_STANDARDS §4 exception): a group holds at most 30 options. */}
        <ul className="flex flex-col divide-y">
          {props.group.options.map((option) => (
            <OptionItem key={option.id} option={option} onEdit={() => setDialog({ option })} />
          ))}
        </ul>
      </CardContent>
      {dialog && (
        <ModifierOptionDialog
          groupId={props.group.id}
          option={dialog.option}
          onClose={() => setDialog(null)}
        />
      )}
    </Card>
  );
}

function OptionItem(props: { option: ModifierOptionView; onEdit: () => void }) {
  const { t, format } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: async (active: boolean) =>
      unwrap(await api.v1["modifier-options"]({ optionId: props.option.id }).patch({ active })),
    onSuccess: (updated) => storeModifierGroup(queryClient, updated),
    onError: (error) => toast.error(catalogueError(t, error)),
  });
  const delta = BigInt(props.option.priceDelta);

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 truncate font-medium">
          {props.option.name}
          {!props.option.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
        </p>
        <p className="text-muted-foreground text-sm tabular-nums">
          {delta > 0n ? `+${format.money(delta)}` : t("catalogue.modifiers.noExtra")}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={props.onEdit}>
          {t("catalogue.group.edit")}
        </Button>
        <ActiveToggle
          size="sm"
          active={props.option.active}
          title={t("catalogue.group.deactivateOptionTitle", { name: props.option.name })}
          description={t("catalogue.group.deactivateOptionHint")}
          confirmLabel={t("catalogue.group.deactivateOption")}
          disabled={toggle.isPending}
          onChange={(active) => toggle.mutate(active)}
        />
      </div>
    </li>
  );
}
